import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';
import config from '@/config';
import log from 'loglevel';
import ToastrService from '@/shared/services/ToastrService';
import EmailEditor from '@/shared/components/EmailEditor';
import { injectStateService } from '@/shared/services/StateService';
import { LinkIcon } from '@/shared/components/Icons';
import moment from 'moment';
import CanRead from '@/shared/components/security/CanRead';
import handleHttpError from '@/shared/services/HandleHttpError';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import { mapSuggestedMailAddress } from '@/shared/services/ObjectDisplayService';
import intlShape from '@/shared/utils/intlShape';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import PlaceholderModal from './PlaceholderModal';
import RecipientPart from './parts/RecipientTypeGroup';
import messages from './emailMessages';
import EmailRecipients from './EmailRecipients';
import {
  combineForRequest,
  createEmptyRecipients,
  getEmailRecipients,
  sanitizeRecipients,
  validateRecipients,
} from './emailHelpers';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
class SingleRelationMailModal extends Component {
  state = {
    email: undefined,
    recipients: createEmptyRecipients(),
    emailAttachments: [],
    templates: [],
    showPlaceholderModal: false,
    employeeAddresses: [],
    loading: false,
  };

  componentDidMount() {
    this.form = React.createRef();
    this.initializeModal();
    this.CanReadDocuments = AuthService.hasPermission({
      functionality: functionalities.Documents,
      privilege: privileges.Read,
    });
  }

  static propTypes = {
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
    emailContext: PropTypes.number.isRequired,
    relationId: PropTypes.string.isRequired,
    invoiceStreamId: PropTypes.string.isRequired,
    companyId: PropTypes.string.isRequired,
    doNotSendMail: PropTypes.bool, //Let the onSave handle sending the mail
    type: PropTypes.oneOf(['task', 'relation']),
    task: PropTypes.shape({
      relation: PropTypes.object,
      relationId: PropTypes.string,
      invoiceStreamId: PropTypes.string,
      id: PropTypes.string,
      parentId: PropTypes.string,
      mailTemplateId: PropTypes.string,
    }),
  };

  static defaultProps = {
    show: false,
    type: 'relation',
    task: undefined,
    invoiceStreamId: null,
    doNotSendMail: false,
  };

  initializeModal = () => {
    // retrieve: email templates for context

    this.setState({ loading: true });
    const { relationId, invoiceStreamId, emailContext, attachments, intl, task } = this.props;
    const templatesPromise = api.base.get(
      `${config.appUrls.api}/api/template/company/${emailContext}`,
    );
    const emailAddressesPromise = getEmailRecipients(relationId, invoiceStreamId, intl);

    Promise.all([templatesPromise, emailAddressesPromise]).then(
      ([{ data: templates }, { addresses, employees }]) => {
        this.setState(
          {
            loading: false,
            emailAttachments: attachments || [],
            templates,
            addresses,
            employees,
            selectedTemplate: task?.mailTemplateId ?? undefined,
          },
          () => {
            if (task?.mailTemplateId) {
              this.retrieveTemplatedEmail();
            }
          },
        );
      },
    );
  };
  sendEmail = emailViewModel => {
    const { intl, onSave } = this.props;
    return api.base
      .post(`${config.appUrls.documents}/api/emails/CreateAndSend`, emailViewModel)
      .then(({ data }) => {
        ToastrService.info(intl.formatMessage(messages.EmailSent));
        return onSave(data);
      })
      .catch(handleHttpError(intl));
  };
  onSave = () => {
    const {
      email,
      emailAttachments,
      recipients,
      selectedTemplate,
      templateLanguageId,
      autoLinkDocumentTypes,
      companyId: invoiceStreamCompany,
    } = this.state;
    const { onSave, intl, relationId, companyId, type, task, doNotSendMail } = this.props;
    const isFormValid = this.validate();

    if (!validateRecipients(recipients)) {
      ToastrService.error(intl.formatMessage(messages.msgInvalidEmailAddress));
      return false;
    }

    let invalidEmailBody = false;

    if (
      !email?.content ||
      // regex to remove line breaks
      email.content.replace(/(\r\n|\n|\r)/gm, '') === '<p></p>'
    ) {
      invalidEmailBody = true;
    }

    if (invalidEmailBody || !isFormValid) {
      ToastrService.error(intl.formatMessage(messages.SubjectBodyRecipientsAreRequired));
      return Promise.reject(new Error('invalid form'));
    }

    const emailViewModel = {
      ...combineForRequest(recipients),
      body: email.content,
      subject: email.title,
      attachmentDocumentIds: emailAttachments.map(x => x.id) || [],
      relationId,
      companyId: invoiceStreamCompany ?? companyId,
      linkId: type === 'task' ? task.id : relationId,
      emailLinkType: type === 'task' ? 2 : 4,
      emailTemplateId: selectedTemplate,
      documentDate: email.documentDate,
      templateLanguageId,
      autoLinkDocumentTypes,
    };
    return doNotSendMail ? onSave(emailViewModel) : this.sendEmail(emailViewModel);
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  renderFreeEmail = () => {
    // we call the templated email because we still want to retrieve to/cc/bcc info from backend
    this.setState(
      state => ({
        ...state,
        selectedTemplate: undefined,
        loading: true,
      }),
      () => this.retrieveTemplatedEmail(),
    );
  };

  emailTitleChanged = title => {
    const { email } = this.state;

    this.setState({
      email: {
        ...email,
        title,
      },
    });
  };

  onEditorStateChange = content => {
    const { email } = this.state;
    this.setState({
      email: {
        ...email,
        content,
      },
    });
  };

  renderSingleEmail = email => {
    const documentUploadEndpoint = AuthService.hasPermission({
      functionality: functionalities.Documents,
      privilege: privileges.Create,
    })
      ? `${config.appUrls.documents}/api/documents?documentType=9&informQueues=false&recognitionStatus=4`
      : `${config.appUrls.documents}/api/documents/Temporary`;
    const { emailAttachments, selectedTemplate } = this.state;
    const { relationId } = this.props;

    const isFreeEmail = !selectedTemplate;

    return (
      <EmailEditor
        title={email.title}
        onTitleChanged={title => this.emailTitleChanged(title)}
        initialValue={email.initialValue}
        onContentChanged={content => this.onEditorStateChange(content)}
        onAttachmentUploaded={(error, file) => this.onDocumentUploaded(error, file)}
        attachmentUploadEndpoint={documentUploadEndpoint}
        attachments={emailAttachments}
        onDeleteAttachment={this.deleteAttachment}
        initialRelation={relationId}
        documentDate={email.documentDate}
        isFreeEmail={isFreeEmail}
        onDocumentDateChanged={documentDate =>
          this.setState({
            email: {
              ...email,
              documentDate,
            },
          })
        }
      />
    );
  };

  onDocumentUploaded = (error, document) => {
    const { intl } = this.props;

    if (error) {
      log.error(error);
      ToastrService.error(intl.formatMessage(messages.MsgEmailAttachmentUploadFailed));
      return;
    }

    const { emailAttachments } = this.state;

    if (emailAttachments.some(x => x.id === document.id)) return;

    this.setState(state => ({
      ...state,
      emailAttachments: [document, ...state.emailAttachments],
    }));
  };

  deleteAttachment = attachment => {
    if (
      AuthService.hasPermission({
        functionality: functionalities.Documents,
        privilege: privileges.Delete,
      }) &&
      (attachment.documentType === 9 || attachment.documentType === 15)
    ) {
      api.base.delete(`${config.appUrls.documents}/api/documents/${attachment.id}`).then(() => {
        this.setState(state => ({
          emailAttachments: state.emailAttachments.filter(x => x.id !== attachment.id),
        }));
      });
    } else {
      this.setState(state => ({
        emailAttachments: state.emailAttachments.filter(x => x.id !== attachment.id),
      }));
    }
  };

  retrieveTemplatedEmail = () => {
    const { relationId, intl, type, task } = this.props;
    const { selectedTemplate } = this.state;

    const relationMailPromise = () =>
      api.base.post(`${config.appUrls.api}/api/template/GetRelationMail`, {
        templateId: selectedTemplate,
        relationId,
      });

    const taskMailPromise = () =>
      api.base.post(`${config.appUrls.api}/api/template/GetTaskMail`, {
        templateId: selectedTemplate,
        subTaskId: task.id,
        relationId: task.relationId,
        invoiceStreamId: task.invoiceStreamId,
      });
    const apiCall = type === 'relation' ? relationMailPromise : taskMailPromise;
    apiCall().then(
      ({ data }) => {
        const {
          cc,
          bcc,
          templateLanguageId,
          recipients: to,
          autoLinkDocumentTypes,
          templateNotFound,
          templateNotAvailableInRelationLanguage,
          companyId,
        } = data;

        // if we got a language id, this means the template was found and available in the relation language
        if (templateLanguageId) {
          const email = {
            title: data.subject || '',
            initialValue: data.body || '',
            content: data.body || '',
            documentDate: moment.utc(),
          };
          this.setState(
            ({ recipients }) => ({
              email,
              recipients: sanitizeRecipients({
                ...recipients,
                to,
                cc,
                bcc,
              }),
              templateLanguageId,
              autoLinkDocumentTypes,
              loading: false,
              companyId,
            }),
            () => this.getTemplateAttachments(templateLanguageId),
          );
        } else {
          if (templateNotFound) {
            if (type === 'relation') {
              ToastrService.error(intl.formatMessage(messages.templateNotFound));
            } else {
              ToastrService.error(intl.formatMessage(messages.templateNotFoundCheckTaskTemplate));
            }
          }
          if (templateNotAvailableInRelationLanguage) {
            ToastrService.error(intl.formatMessage(messages.msgTemplateNotInLanguage));
          }
          this.setState(({ recipients }) => ({
            recipients: sanitizeRecipients({
              ...recipients,
              to,
              cc,
              bcc,
            }),
            loading: false,
            selectedTemplate: undefined,
            companyId,
            email: {
              title: data.subject || '',
              initialValue: data.body || '',
              content: data.body || '',
              documentDate: moment.utc(),
            },
          }));
        }

        if (this.CanReadDocuments && autoLinkDocumentTypes && autoLinkDocumentTypes.length > 0) {
          const mainTaskId = task.parentId || task.id;
          let linkIds = [mainTaskId];

          api.base
            .get(`${config.appUrls.tasks}/api/plannedtasks/ChildTaskIds/${mainTaskId}`)
            .then(({ data: childTaskIds }) => {
              linkIds = [...linkIds, ...childTaskIds];
              this.retrieveTaskDocumentsForTemplate(linkIds, autoLinkDocumentTypes);
            })
            .catch(error => {
              this.retrieveTaskDocumentsForTemplate(linkIds, autoLinkDocumentTypes);
            });
        }
      },
      error => {
        this.setState({ loading: false });
        handleHttpError(intl)(error);
      },
    );
  };

  templateChanged = item => {
    const template = item?.key;

    this.setState(
      state => ({
        ...state,
        selectedTemplate: template,
        loading: !!template,
        email: template ? undefined : state.email,
      }),
      () => {
        if (template) {
          this.retrieveTemplatedEmail();
        }
      },
    );
  };

  getTemplateAttachments = templateLanguageId => {
    const { attachments } = this.props;
    if (!this.CanReadDocuments) return Promise.resolve();
    return api.base
      .get(`${config.appUrls.documents}/api/Documents/Links/${templateLanguageId}/4`)
      .then(({ data }) =>
        this.setState({
          emailAttachments: [...(attachments || []), ...data.flat()],
        }),
      );
  };

  retrieveTaskDocumentsForTemplate = (linkIds, autoLinkDocumentTypes) =>
    api.base
      .post(`${config.appUrls.documents}/api/documents/GetLinks`, linkIds)
      .then(({ data }) => {
        if (data && data.length > 0) {
          const attachments = [];
          data.forEach(attachment => {
            if (autoLinkDocumentTypes.some(x => x === attachment.documentType))
              attachments.push(attachment);
          });

          this.setState(state => ({
            ...state,
            emailAttachments: [...attachments, ...state.emailAttachments],
          }));
        }
      });

  validate() {
    const { email } = this.state;
    if (!email) return false;
    if (!this.form || !this.form.current) return false;

    return this.form.current.reportValidity();
  }

  handleRecipientChange = (type, newValues) => {
    this.setState(({ recipients }) => ({
      recipients: {
        ...recipients,
        [type]: newValues || [],
      },
    }));
  };

  render() {
    const { show, intl, stateService, emailContext } = this.props;
    const {
      email,
      templates,
      selectedTemplate,
      showPlaceholderModal,
      addresses,
      employees,
      recipients,
      loading,
    } = this.state;
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.SendEmail)}
        show={show}
        onClose={this.closeModal}
        onSave={this.onSave}
        size="xl"
        disabled={loading}
      >
        {showPlaceholderModal && (
          <PlaceholderModal
            onSave={() => this.setState({ showPlaceholderModal: false })}
            onClose={() => this.setState({ showPlaceholderModal: false })}
            templateContext={emailContext}
          />
        )}

        {show && (
          <form ref={this.form}>
            <div className="form-horizontal container-fluid">
              <div className="form-group">
                <label className="control-label col-sm-3">
                  {intl.formatMessage(messages.Template)}
                </label>
                <div className="col-md-6">
                  {!templates.length && (
                    <label className="control-label">
                      {intl.formatMessage(messages.NoTemplatesFound)}
                    </label>
                  )}
                  {templates.length > 0 && (
                    <AutoComplete
                      items={templates
                        .map(template => ({
                          key: template.id,
                          label: intl.formatMessage(
                            listItemMsg('template', template.id, template.name),
                          ),
                        }))
                        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))}
                      initialSelectedItem={
                        selectedTemplate
                          ? templates
                              .map(template => ({
                                key: template.id,
                                label: intl.formatMessage(
                                  listItemMsg('template', template.id, template.name),
                                ),
                              }))
                              .find(item => item.key === selectedTemplate)
                          : undefined
                      }
                      placeholder={intl.formatMessage(messages.ChooseTemplate)}
                      mode="edit"
                      onChange={this.templateChanged}
                      onClear={() => this.templateChanged(null)}
                    />
                  )}
                </div>
                <span className="col-md-2">
                  <CanRead on="settings">
                    <button
                      type="button"
                      className="btn btn-default"
                      title={intl.formatMessage(messages.OpenEmailTemplates)}
                      onClick={() => stateService.goToEmailTemplates()}
                    >
                      <LinkIcon className="margin5R" />
                      <FormattedMessage {...messages.Templates} />
                    </button>
                  </CanRead>
                </span>
              </div>

              {email && (
                <EmailRecipients
                  value={recipients}
                  addresses={addresses}
                  employees={employees}
                  onChange={this.handleRecipientChange}
                />
              )}
              {!selectedTemplate && !email && (
                <div className="form-group padding10T">
                  <span
                    className="col-sm-offset-3 col-md-8 text-clickable"
                    onClick={this.renderFreeEmail}
                  >
                    {intl.formatMessage(messages.ClickHereToCreateFreeTextEmail)}
                  </span>
                </div>
              )}

              {email && this.renderSingleEmail(email)}
              {loading && <LoadingAnimation />}
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}

export default injectStateService(SingleRelationMailModal);
